/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 *
 * Angular Island voor de simulatie-canvas, opvolger van de
 * AngularJS-controller `cytocanvas.simview`.
 *
 * Golf 1 (Stap 2c, zie `migratieplan.md`) bracht de state-rendering en het basis-afspelen
 * (starten/stoppen/volgende stap) hierheen over: pure `cy`-manipulatie/state-machine-logica
 * zonder ng1-framework-afhankelijkheid.
 *
 * Golf 2a bracht de toestandsselectie hierheen over (`selectAllStates`/`selectNone`/
 * `selectEndings`/`selectPad`/`checkStateSelect`): pure `cy`-queries zonder `modelctrl`- of
 * samenwerkings-afhankelijkheid.
 *
 * Golf 5c bracht de resultaatweergave hierheen over (`showResult` + `SimResultaatTekenaar`) en
 * belegt het eigendom van het `simulatie`-object bij dit component. Golf 5d/5e brachten de value-
 * en ineq-history hierheen (`toonValuehistory`/`toonIneqhistory` + de multi-select-lus in
 * `showResult`).
 *
 * Golf 3d maakt het component eigenaar van de simulatiestatus en de sim-selectie: de
 * selectie-events op de eigen `cy`, de selectievolgorde-teller, de leider/volger-routes rond een
 * selectie- of positiewijziging, en het toepassen van een binnengekomen canvasstand. De
 * gedeelde state (selectievlaggen, history-standen, volgende stap, `simuleren`) schrijft het naar
 * de `SimulatieStatusService`, waar `dl-sim-toolbar` hem leest; de wire-vorm eromheen stelt de
 * samenwerk-baas samen (sinds golf 4f ook het verzenden, dat tot dan via de migratie-interface liep).
 * Sinds golf 4e is ook de laatste ng1-lijm weg: de splitter-richting
 * komt uit de `SchermindelingService`, het eiland meldt zich aan bij de `GlobalSimcanvasService` en het
 * let zelf op de samenwerkwissels waarna de stand rondgestuurd moet worden.
 */
import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import type { CytoscapeOptions, ElementsDefinition, NodeSingular, Position } from 'cytoscape';
import { DlCytoCanvasBaseDirective } from '../dl-cytocanvas-base/dl-cytocanvas-base.directive';
import type { CytoCanvasView } from '../../../app/canvas/canvasregistratie.service';
import type { Simulatie_eiland } from '../../../app/samenwerking/protocol.types';
import { SamenwerkprotocolService } from '../../../app/samenwerking/samenwerkprotocol.service';
import { DlModel } from '../../../app/model/dl-model';
import { GlobalSimcanvasService } from '../../../app/simulatie/global-simcanvas.service';
import { SamenwerkingService } from '../../../services/samenwerking.service';
import { SchermindelingService } from '../../../services/schermindeling.service';
import { SimulatieStatusService } from '../../../services/simulatie-status.service';
import type { SimSelectieLog, SimSelectieOverdracht } from '../../../services/simulatie-status.service';
import { elements } from 'dynalearn-common/lib/elements';
import { cytoSimStyle } from './sim-style';
import { SimResultaatTekenaar } from './sim-resultaat-tekenaar';
import type { SimResultaatData } from './sim-resultaat-tekenaar';
import { stateVanNode } from './sim-lookup';

/**
 * Eén transitie (terminatie/ordening/closure) binnen een simulatiestate, zoals aangeleverd
 * door het backend. Vorm verder ongewijzigd overgenomen uit de ng1-simulatiedata.
 */
export interface SimulatieTransitie extends SimResultaatData {
    identifier?: string;
    statenum?: number;
    transition_type?: string;
}

/** Eén closure-item van een state: `to` bevat de opvolgende statenummers (leeg = eindpunt). */
export interface SimulatieClosure extends SimulatieTransitie {
    to: unknown[];
}

/**
 * Eén state binnen de simulatiedata. Draagt behalve zijn structuur (de transities en de buren) ook de
 * waarden en (on)gelijkheden van die toestand: sinds golf 3.6 leest de tekenaar ze hiervandaan in
 * plaats van uit een kopie in de node-data (zie `sim-lookup.ts`).
 */
export interface SimulatieState extends SimResultaatData {
    num: number;
    start?: boolean;
    terminaties: SimulatieTransitie[];
    orderings: SimulatieTransitie[];
    closures: SimulatieClosure[];
    from: number[];
    to: number[];
    d2values: Record<string, string>;
    d3values: Record<string, string>;
}

/**
 * Eén ineq-history-entry (per model-(on)gelijkheid) binnen de simulatie: welke ineq, of hij zichtbaar
 * is, of het een afgeleide betreft, de argumenten (`from`/`to`), het per-state gevonden ineq-type
 * (cache) en de opgeslagen linkerbovenhoek van het historyblok. Leeft in `simulatie.ineqHistory`; in de
 * samenwerkings-/replay-status reist hij sinds 29-09-2026 los mee als `ineqhistories`, want de simulatie
 * zelf gaat sinds 17-09-2026 niet meer over de lijn (`SamenwerkprotocolService.haalSimulatiestatus`).
 */
export interface IneqHistoryEntry {
    visible: boolean;
    ineq: string; // node-id van de model-(on)gelijkheid, voor de plaatsing
    derivative: boolean;
    from: string;
    to: string;
    perState: Record<number, string | false>;
    historylefttop?: { left: number; top: number };
}

/** De volledige simulatiedata zoals aangeleverd door het backend (`simdata`). */
export interface SimulatieData {
    start: number[];
    states: Record<string, SimulatieState>;
    savedPos?: Record<string, Position>;
    ineqHistory?: Record<string, IneqHistoryEntry>;
    [key: string]: unknown;
}

/** De eerstvolgende stap-soort in de simulatie, gebruikt voor de knopklasse in `dl-sim-toolbar`. */
export type SimStapSoort = 'start' | 'terminate' | 'order' | 'close' | 'finished';

/** Vlaggen voor de vier selectieknoppen in de sim-toolbar, berekend door `berekenStateSelectie()`. */
export interface StateSelectieVlaggen {
    alles: boolean;
    niets: boolean;
    endings: boolean;
    pad: boolean;
}

@Component({
    selector: 'dl-simcanvas',
    standalone: true,
    templateUrl: './dl-simcanvas.component.html',
    styleUrls: [
        '../dl-cytocanvas-base/dl-cytocanvas-base.scss',
        './dl-simcanvas.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DlSimCanvasComponent extends DlCytoCanvasBaseDirective implements Simulatie_eiland {

    /**
     * View-naam waaronder dit canvas zich registreert bij de `CanvasregistratieService`.
     */
    protected readonly viewKey: CytoCanvasView = 'sim';

    /**
     * De samenwerk-baas, die de simulatiestatus samenstelt en naar de samenwerkers stuurt.
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /** De query-/mutatie-API over de modelgraaf; nodig voor de resultaatweergave (golf 5c). */
    private readonly model = inject(DlModel);

    /** De gedeelde sim-sessiestate: dit component schrijft, `dl-sim-toolbar` leest (golf 3d). */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /** De samenwerkrol, die bepaalt of een gebaar een verzoek is of meteen wordt uitgevoerd. */
    private readonly samenwerking = inject(SamenwerkingService);

    /** De verdeling van het bouwscherm; de lay-out van de toestanden hangt aan de richting. */
    private readonly schermindeling = inject(SchermindelingService);

    /** Waar dit eiland zich aanmeldt, zodat de `SimulatieService` erbij kan. */
    private readonly globalSimcanvas = inject(GlobalSimcanvasService);

    /**
     * Of er een simulatie gemaakt wordt; de template legt dan een spinner over het canvas. Bij de
     * uitvoerder én bij zijn volgers (golf 3.6, testronde 16-09-2026).
     */
    protected readonly genereren = this.simulatieStatus.genereren;

    /** De momenteel geladen simulatie, of `null` als er niet gesimuleerd wordt. */
    private simulatie: SimulatieData | null = null;

    /**
     * Teller die de volgorde van (de)selecteren vastlegt, als node-data op elke state. De
     * histories sorteren erop wanneer er geen padvolgorde geldt, en hij reist mee over de lijn
     * zodat leider en volgers dezelfde volgorde tonen.
     */
    private selectievolgorde = 0;

    /**
     * Onderdrukt de selectie-afhandeling tijdens programmatische mutaties (json-herlaad, de
     * selectieknoppen, een overgenomen volger-selectie): die paden wikkelen zelf af — teller,
     * knoppenstate, log en verzending — en willen geen afhandeling per losse node.
     */
    private eventLock = false;

    /** Timer die een reeks selectie-events tot één afhandeling bundelt. */
    private selectiePlanner: ReturnType<typeof setTimeout> | null = null;

    /** Timer die een reeks statuswijzigingen tot één verzending bundelt. */
    private statusPlanner: ReturnType<typeof setTimeout> | null = null;

    /**
     * Vorige simulatie + posities, voor het herstellen van posities bij een identieke herstart. De
     * `invoerhash` is sinds golf 3.6 de maatstaf voor "identiek": het backend levert hem bij de
     * simulatie, en gelijke invoer betekent per definitie hetzelfde resultaat.
     */
    private prevsim: {
        simulatie: SimulatieData | null;
        invoerhash: string | null;
        finished: boolean;
        positions: Record<string, Position> | false;
    } = {
        simulatie: null,
        invoerhash: null,
        finished: false,
        positions: false
    };

    /** De invoerhash van de simulatie die nu getekend staat; zie `prevsim`. */
    private huidigeInvoerhash: string | null = null;

    /** De resultaattekenaar (golf 5c); lazy geïnstantieerd zodra de sim-`cy` bestaat. */
    private tekenaarInstance: SimResultaatTekenaar | null = null;

    /**
     * Callback die de (ng21) prullenbak via de container-scratch aanroept om een value-history te
     * verbergen.
     */
    private readonly verbergValuehistoryCb = (container: NodeSingular): void => {
        this.verbergValuehistoryVanaf(container.id());
    };

    /**
     * Callback die de (ng21) prullenbak via de container-scratch aanroept om een ineq-history te
     * verbergen.
     */
    private readonly verbergIneqhistoryCb = (container: NodeSingular): void => {
        this.verbergIneqhistoryVanaf(container.data('historyineq'));
    };

    constructor() {
        super("debug");
        //bij een rolwissel of een nieuwe deelnemer moet de leider zijn simulatiestand rondsturen. Tot
        //golf 4e deed de ng1-simview dat op `model.werkmodusChanged` en `api.notify.clientOnline`;
        //beide standen staan in de SamenwerkingService, dus lezen is genoeg. Bewust gepland en niet
        //direct: een bericht dat bij de ontvanger vóór zijn eigen rolwissel aankomt, valt daar in de
        //retry van de dispatcher. Het versturen guard't zelf op de rol.
        effect(() => {
            this.samenwerking.werkmodus();
            this.samenwerking.clientnames();
            if (this.cyInstance) {
                this.planStatusVersturen();
            }
        });
    }

    /**
     * Koppelt naast de basisinitialisatie de eigen canvas-events: de selectie van states (die de
     * weergave, het actielog en de samenwerking aanstuurt) en het einde van een sleepbeweging. Meldt
     * zich ook aan als het simcanvas van de applicatie.
     */
    override ngOnInit(): void {
        super.ngOnInit();
        this.globalSimcanvas.setEiland(this);
        const cy = this.cyInstance;
        if (!cy) return;
        cy.on('select unselect', 'node', (event) => this.verwerkSelectie(event.target as NodeSingular));
        cy.on('endAutomove', (_event, automove: {mainnode?: NodeSingular; movegroup?: cytoscape.CollectionReturnValue}) =>
            this.verwerkVerplaatsing(automove));
    }

    /**
     * Ruimt naast het canvas ook de lopende timers en de aanmelding op.
     */
    override ngOnDestroy(): void {
        if (this.selectiePlanner) {
            clearTimeout(this.selectiePlanner);
            this.selectiePlanner = null;
        }
        if (this.statusPlanner) {
            clearTimeout(this.statusPlanner);
            this.statusPlanner = null;
        }
        this.globalSimcanvas.clearEiland(this);
        super.ngOnDestroy();
    }

    /**
     * De resultaattekenaar, lazy geïnstantieerd zodra de sim-`cy` bestaat. Bezit `DlModel`, de
     * sim-`cy` en de logger; `null` zolang er nog geen `cy` is.
     */
    private get tekenaar(): SimResultaatTekenaar | null {
        if (!this.tekenaarInstance && this.cyInstance) {
            this.tekenaarInstance = new SimResultaatTekenaar(this.model, this.cyInstance, this.log);
        }
        return this.tekenaarInstance;
    }

    /**
     * Vult de Cytoscape-opties aan met de simulatie-specifieke stylesheet en
     * de interactie-instellingen die in de oude AngularJS-controller stonden.
     */
    protected override configureerCanvas(opties: CytoscapeOptions): CytoscapeOptions {
        return {
            ...opties,
            style: cytoSimStyle,
            zoomingEnabled: true,
            userZoomingEnabled: true,
            panningEnabled: true,
            userPanningEnabled: true,
            boxSelectionEnabled: false,
            minZoom: 0.25,
            maxZoom: 4,
            selectionType: 'single',
            touchTapThreshold: 8,
            desktopTapThreshold: 10,
            autolock: false,
            autoungrabify: false,
            autounselectify: false,
            hideLabelsOnViewport: true,
            motionBlur: true,
            textureOnViewport: true,
            pixelRatio: 'auto'
        };
    }

    /**
     * Begin met het afbeelden van de simulatiestates, gegeven de meegestuurde simulatie.
     * @param simdata Simulatiedata volgens het format van het backend
     * @param fullsim Als `true`, dan beelden we gelijk de volle simulatie af qua states
     * @param invoerhash De hash die het backend bij deze simulatie levert; gelijke invoer betekent
     *   dezelfde simulatie, en dan mogen de posities van de vorige keer terug
     */
    public startSimulatie(simdata: SimulatieData, fullsim: boolean, invoerhash?: string | null): void {
        const cy = this.cyInstance;
        if (!cy) return;

        let restorepos: Record<string, Position> | false = false;
        if (fullsim && simdata && this.prevsim.finished) {
            //vergelijken op de hash in plaats van op twee volledige serialisaties van de simulatie:
            //dat waren bij een groot model megabytes aan `JSON.stringify` in de UI-thread, bovenop het
            //parsen. Zonder hash (een backend van vóór golf 3.6) herstellen we niets — dat kost hoogstens
            //een lay-out, terwijl een verkeerde gok de posities van een ándere simulatie zou opleggen
            const zelfde = !!invoerhash && invoerhash === this.prevsim.invoerhash;
            if (zelfde) restorepos = this.prevsim.positions;
        }

        this.reset();
        this.simulatie = simdata;
        this.huidigeInvoerhash = invoerhash ?? null;
        if (fullsim) {
            this.simulateFull();
        } else {
            cy.add({
                group: 'nodes',
                position: {x: 50, y: 50},
                data: {type: 'state', state: null, id: 'sn0', num: 0, geplaatst: true},
                classes: 'scenario'
            }).select();
            for (const statenum of this.simulatie.start) {
                this.addState(this.simulatie.states['s' + statenum], 'open');
            }
            this.updateNextStep();
            this.drawPositions();
        }
        if (restorepos) {
            for (const id of Object.keys(restorepos)) {
                cy.$id(id).position({...restorepos[id]});
            }
            this.initZoomEnFit();
        }
        this.planStatusVersturen();
    }

    /**
     * Maak een volle simulatie: alle states in één keer, gesloten.
     */
    private simulateFull(): void {
        const cy = this.cyInstance;
        if (!cy || !this.simulatie) return;
        cy.add({
            group: 'nodes',
            position: {x: 50, y: 50},
            data: {type: 'state', state: null, id: 'sn0', num: 0, geplaatst: true},
            classes: 'scenario'
        }).select();
        for (const s of Object.values(this.simulatie.states)) {
            this.addState(s, 'closed');
        }
        this.updateNextStep();
        this.doLayout();
    }

    /**
     * Doe de volgende stap in de simulatie: open states worden getermineerd, etc.
     */
    public nextStep(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        this.tekenaar?.clearResult(this.simulatie);
        cy.nodes().unselect();

        // flaggen zodat closures die nieuwe (open) states opleveren niet meteen in dezelfde
        // stap alweer getermineerd worden
        cy.nodes().forEach(node => { node.data('bestaat', true); });

        cy.nodes('[status = "ordered"]').forEach(s => this.simulateCloseState(s as NodeSingular));
        cy.nodes('[status = "terminated"]').forEach(s => this.simulateTransitie(s as NodeSingular, 'ordering'));
        cy.nodes('[status = "open"][bestaat]').forEach(s => this.simulateTransitie(s as NodeSingular, 'terminatie'));

        this.updateNextStep();
        this.drawPositions();
        this.planStatusVersturen();
    }

    /**
     * Bepaalt de eerstvolgende stap-soort uit de aanwezige statussen in de simulatie en zet hem in
     * de gedeelde sim-state, waar de toolbar zijn knopklasse uit leest.
     */
    private updateNextStep(): void {
        const cy = this.cyInstance;
        if (!this.simulatie || !cy || cy.nodes().empty()) {
            this.simulatieStatus.zetVolgendeStap('start');
        } else if (cy.nodes('[status = "open"]').nonempty()) {
            this.simulatieStatus.zetVolgendeStap('terminate');
        } else if (cy.nodes('[status = "terminated"]').nonempty()) {
            this.simulatieStatus.zetVolgendeStap('order');
        } else if (cy.nodes('[status = "ordered"]').nonempty()) {
            this.simulatieStatus.zetVolgendeStap('close');
        } else {
            this.simulatieStatus.zetVolgendeStap('finished');
        }
    }

    /**
     * Teken het resultaat van de huidige stateselectie op de modelgraaf, inclusief de value- en
     * ineq-history-lus bij multi-select.
     * @param forceRegenerate Als `true`, dan worden de opgeslagen simnodes niet hergebruikt
     */
    private showResult(forceRegenerate?: boolean): void {
        this.tekenaar?.showResult(this.simulatie, forceRegenerate);
        this.toonValuehistories(forceRegenerate);
        this.toonIneqhistories(forceRegenerate);
    }

    /**
     * Zet alle value-histories aan of uit (toolbarknop) en stuurt de nieuwe stand rond. De
     * samenwerk-baas (`simulatietabel`) logt en gate't de volgmodus, dus hier komt alleen leider/lokaal langs.
     */
    public toggleAlleValuehistory(): void {
        const toon = !this.simulatieStatus.history().value;
        this.simulatieStatus.zetToonValuehistory(toon);
        if (toon) {
            this.toonAlleValuehistory();
        } else {
            this.verbergAlleValuehistory();
        }
        this.planStatusVersturen();
    }

    /**
     * Verbergt de value-history van één container: bij een volger als verzoek aan de leider, anders
     * meteen — met de toolbarvlag en de statusverzending eromheen. Aangeroepen door de prullenbak
     * en, bij een volger-verzoek, door de leider via de dispatcher.
     * @param containerId het id van de container-node van de history
     */
    public verbergValuehistoryVanaf(containerId: string): void {
        if (this.samenwerking.isVolger()) {
            this.simulatieStatus.vraagVerbergValuehistory(containerId);
            return;
        }
        this.simulatieStatus.zetToonValuehistory(this.verbergValuehistory(containerId));
        this.planStatusVersturen();
    }

    /**
     * Toon alle value-histories: teken per quantity de history en markeer dat hij aanstaat. Vervangt
     * de ng1 `showAllValueHistory`.
     */
    private toonAlleValuehistory(): void {
        const tekenaar = this.tekenaar;
        if (!tekenaar) return;
        const states = this.berekenHistoryStates();
        for (const qNode of this.model.quantities().toArray() as unknown as NodeSingular[]) {
            tekenaar.showHistory(this.simulatie, qNode, states, false, this.verbergValuehistoryCb);
            qNode.scratch('showvaluehistory', true);
        }
    }

    /**
     * Verberg alle value-histories: verwijder de history-nodes en zet de toon-markering per quantity
     * uit. Vervangt de ng1 `hideAllValueHistory`; ook aangeroepen vanuit `reset()`.
     */
    private verbergAlleValuehistory(): void {
        this.model.removeSimulation('[historyquantity]');
        for (const qNode of this.model.quantities().toArray() as unknown as NodeSingular[]) {
            qNode.scratch('showvaluehistory', false);
        }
    }

    /**
     * Verberg de value-history van één container: verwijder de nodes en zet de toon-markering uit.
     * Geeft terug of er nog value-history over is, zodat de toolbarvlag bijgewerkt kan worden.
     * @param historyNodeId het id van de container-node
     */
    private verbergValuehistory(historyNodeId: string): boolean {
        const historyNode = this.model.getElement(historyNodeId);
        const qid = historyNode.data('historyquantity');
        this.model.removeSimulation('[historyquantity="' + qid + '"]');
        this.model.getElement(qid).scratch('showvaluehistory', false);
        return this.model.simulationContent('[historyquantity]').nonempty();
    }

    /**
     * Herteken de zichtbare value-histories na een `showResult`, maar alleen bij meer dan één
     * geselecteerde echte state.
     */
    private toonValuehistories(forceRegenerate?: boolean): void {
        const cy = this.cyInstance;
        const tekenaar = this.tekenaar;
        if (!cy || !tekenaar) return;
        if (cy.nodes('node[type = "state"][id != "sn0"]:selected').length <= 1) return;
        const states = this.berekenHistoryStates();
        for (const qNode of this.model.quantities().toArray() as unknown as NodeSingular[]) {
            if (qNode.scratch('showvaluehistory')) {
                tekenaar.showHistory(this.simulatie, qNode, states, forceRegenerate, this.verbergValuehistoryCb);
            }
        }
    }

    /**
     * De state-nodes op tekenvolgorde voor de histories: eerst de padvolgorde, anders de geselecteerde
     * states gesorteerd op selectievolgorde.
     */
    private berekenHistoryStates(): NodeSingular[] {
        const cy = this.cyInstance;
        if (!cy) return [];
        const selectie = cy.nodes('node[type = "state"][id != "sn0"]:selected');
        // de padvolgorde (van de knop 'gedragspad') alleen gebruiken als hij exact de huidige selectie
        // dekt. Wijkt de selectie af — bijv. na 'gedragspad' handmatig een state toegevoegd/weggehaald —
        // dan vervalt de pad-modus en sorteren we op selectievolgorde. Een pad mag states herhalen
        // (cyclus), dus we vergelijken op de verzameling ids, niet op lengte.
        const pad = this.statesVolgensPadvolgorde(true);
        if (pad) {
            const selectieIds = new Set(selectie.map((s) => s.id()));
            const padIds = new Set(pad.map((s) => s.id()));
            if (padIds.size === selectieIds.size && [...padIds].every((id) => selectieIds.has(id))) {
                return pad;
            }
        }
        return selectie
            .sort((a, b) => (Number(a.data('selectievolgorde')) || 0) - (Number(b.data('selectievolgorde')) || 0))
            .toArray() as unknown as NodeSingular[];
    }

    /**
     * Zet alle ineq-histories aan of uit (toolbarknop) en stuurt de nieuwe stand rond. Zelfde
     * route als `toggleAlleValuehistory`.
     */
    public toggleAlleIneqhistory(): void {
        const toon = !this.simulatieStatus.history().ineq;
        this.simulatieStatus.zetToonIneqhistory(toon);
        if (toon) {
            this.toonAlleIneqhistory();
        } else {
            this.verbergAlleIneqhistory();
        }
        this.planStatusVersturen();
    }

    /**
     * Verbergt de ineq-history van één container: bij een volger als verzoek aan de leider, anders
     * meteen — met de toolbarvlag en de statusverzending eromheen.
     * @param ineqkey de key (`from-to`) van de (on)gelijkheid
     */
    public verbergIneqhistoryVanaf(ineqkey: string): void {
        if (this.samenwerking.isVolger()) {
            this.simulatieStatus.vraagVerbergIneqhistory(ineqkey);
            return;
        }
        this.simulatieStatus.zetToonIneqhistory(this.verbergIneqhistory(ineqkey));
        this.planStatusVersturen();
    }

    /**
     * Toon alle ineq-histories: bouw de `ineqHistory`-map uit de model-(on)gelijkheden (zonder
     * calculus-argumenten) en teken per (on)gelijkheid de history. Vervangt de ng1 `showAllIneqHistory`.
     */
    private toonAlleIneqhistory(): void {
        const tekenaar = this.tekenaar;
        if (!tekenaar || !this.simulatie) return;
        if (!this.simulatie.ineqHistory) {
            this.simulatie.ineqHistory = {}; // key = ineq-args, value = object met data
        }
        const ineqHistory = this.simulatie.ineqHistory;
        const states = this.berekenHistoryStates();
        for (const ineq of this.model.modelIneqs().toArray() as unknown as NodeSingular[]) {
            const fromEl = this.model.getElement(ineq.data('from'));
            const toEl = this.model.getElement(ineq.data('to'));
            // geen calculus
            if (elements.isSubtypeOf(fromEl.data('type'), 'calc') || elements.isSubtypeOf(toEl.data('type'), 'calc')) {
                continue;
            }
            const ineqkey = ineq.data('from') + '-' + ineq.data('to');
            if (!ineqHistory[ineqkey]) {
                ineqHistory[ineqkey] = {
                    visible: true, // zometeen zichtbaar
                    ineq: ineq.id(), // id bewaren voor het plaatsen
                    derivative: elements.isSubtypeOf(ineq.data('type'), 'd_ineq'), // is dit een afgeleide
                    from: ineq.data('from'),
                    to: ineq.data('to'),
                    perState: {} // type per state, wordt opgeslagen als we hem vinden
                };
            }
            ineqHistory[ineqkey].visible = true; // ook als hij al bestond
            tekenaar.showIneqHistory(this.simulatie, ineqkey, ineqHistory[ineqkey], states, false,
                this.verbergIneqhistoryCb);
        }
    }

    /**
     * Verberg alle ineq-histories: verwijder de history-nodes en zet elke entry op onzichtbaar.
     * Vervangt de ng1 `hideAllIneqHistory`.
     */
    private verbergAlleIneqhistory(): void {
        this.model.removeSimulation('[historyineq]');
        if (this.simulatie?.ineqHistory) {
            for (const ineqkey of Object.keys(this.simulatie.ineqHistory)) {
                this.simulatie.ineqHistory[ineqkey].visible = false;
            }
        }
    }

    /**
     * Verberg de ineq-history van één container (op ineq-key): verwijder de nodes en zet de entry op
     * onzichtbaar. Geeft terug of er nog ineq-history over is, zodat de toolbarvlag bijgewerkt kan
     * worden.
     * @param ineqkey de key (`from-to`) van de (on)gelijkheid
     */
    private verbergIneqhistory(ineqkey: string): boolean {
        this.model.removeSimulation('[historyineq="' + ineqkey + '"]');
        if (this.simulatie?.ineqHistory?.[ineqkey]) {
            this.simulatie.ineqHistory[ineqkey].visible = false;
        }
        return this.model.simulationContent('[historyineq]').nonempty();
    }

    /**
     * Herteken de zichtbare ineq-histories na een `showResult`, maar alleen bij meer dan één
     * geselecteerde echte state.
     */
    private toonIneqhistories(forceRegenerate?: boolean): void {
        const cy = this.cyInstance;
        const tekenaar = this.tekenaar;
        if (!cy || !tekenaar || !this.simulatie?.ineqHistory) return;
        if (cy.nodes('node[type = "state"][id != "sn0"]:selected').length <= 1) return;
        const ineqHistory = this.simulatie.ineqHistory;
        const states = this.berekenHistoryStates();
        for (const ineqkey of Object.keys(ineqHistory)) {
            if (ineqHistory[ineqkey].visible) {
                tekenaar.showIneqHistory(this.simulatie, ineqkey, ineqHistory[ineqkey], states, forceRegenerate,
                    this.verbergIneqhistoryCb);
            }
        }
    }

    /**
     * Herbouwt de layout van de stategraph. Volgers doen alleen een fit (de leider bepaalt
     * de posities en stuurt die door).
     */
    public doLayout(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        cy.resize();

        if (this.samenwerking.isVolger()) {
            this.initZoomEnFit();
            return;
        }

        cy.nodes('[type = "state"],[type = "transitie"]').removeData('geplaatst');
        const scenario = cy.getElementById('sn0');
        scenario.position({x: 50, y: 50});
        scenario.data('geplaatst', true);
        this.drawPositions();
        this.initZoomEnFit();
        this.planStatusVersturen(false, true);
    }

    /**
     * Probeer de states zo neer te zetten dat er zo weinig mogelijk transitie-lijnen kruisen:
     * draai herhaald een `cose`-layout en bewaar de variant met de minste kruisingen, tot er
     * `wachtnabeste` ms geen betere meer gevonden is (of tot er nul kruisingen zijn).
     * `SimulatieService.mooieStates` zet het simcanvas eromheen op slot en laat daarna de
     * simulatiestatus rondsturen.
     */
    public async niceStates(): Promise<void> {
        const cy = this.cyInstance;
        if (!cy) return;

        this.log.debug('niceStates');

        //het slot van het simcanvas (`setReadonly`) is `autolock`, en een layout verplaatst geen
        //vergrendelde nodes: dan verandert er niets. Tijdens het neerleggen gaat dat slot er daarom af,
        //en houden ungrabify en unselectify de gebruiker er toch vanaf
        const locked = cy.autolock();
        const ungrabify = cy.autoungrabify();
        const unselectify = cy.autounselectify();
        cy.autolock(false);
        cy.autoungrabify(true);
        cy.autounselectify(true);

        const wachtnabeste = 1500; // zoveel ms nadat we een nieuwe beste hebben zoeken we naar een betere
        let bestkruisingen = 99999;
        const bestposities: Record<string, Position> = {};

        const runLayout = (): Promise<void> => new Promise(resolve => {
            this.log.debug(`runLayout`);
            cy.layout({
                name: 'cose',
                animate: false,
                randomize: true, // nodig voor eerste positie
                fit: false, // doen we later
                stop: () => {this.log.debug(`layout klaar`); resolve()} // klaar
            } as cytoscape.LayoutOptions).run();
        });

        let bestemeting = Date.now();
        while (Date.now() - bestemeting < wachtnabeste) {
            await runLayout();
            const kruisingen = this.alleKruisingen().length;
            this.log.debug(`Kruisingen:`, kruisingen);
            if (kruisingen < bestkruisingen) {
                // deze is beter
                cy.nodes().forEach(node => {
                    bestposities[node.id()] = {...node.position()};
                });
                bestkruisingen = kruisingen;
                if (kruisingen === 0) {
                    break; // klaar
                }
                bestemeting = Date.now();
            }
        }
        // goed zetten
        for (const id of Object.keys(bestposities)) {

            cy.$id(id).position(bestposities[id]);
        }
        cy.autolock(locked);
        cy.autoungrabify(ungrabify);
        cy.autounselectify(unselectify);
        this.initZoomEnFit();
    }

    /**
     * Reset de hele simulatie zoals de gebruiker hem sluit: bewaart de huidige posities (voor een
     * eventuele identieke herstart), verwijdert alle simulatie-elementen en de resultaatweergave.
     * @param skipSamenwerking Als `true`, dan wordt de simulatiestatus niet rondgestuurd
     */
    public reset(skipSamenwerking?: boolean): void {
        if (!this.cyInstance) return;
        this.bewaarVoorHerstart();
        this.wisSimulatie();
        if (!skipSamenwerking) {
            this.planStatusVersturen();
        }
    }

    /**
     * Bewaart de huidige stand voor een eventuele herstart van dezelfde simulatie: die begint dan
     * op de posities waar de gebruiker hem achterliet in plaats van op een verse layout.
     */
    private bewaarVoorHerstart(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        this.prevsim.finished = this.simulatieStatus.volgendeStap() === 'finished';
        this.prevsim.simulatie = this.simulatie;
        this.prevsim.invoerhash = this.huidigeInvoerhash;
        this.prevsim.positions = {};
        cy.nodes().forEach(node => {
            (this.prevsim.positions as Record<string, Position>)[node.id() ?? '-'] = {...node.position()};
        });
    }

    /**
     * Haalt de simulatie van het canvas: de resultaatweergave, de nodes, de histories en de
     * afgeleide stand. Zendt niets en raakt de herstart-cache niet aan.
     */
    private wisSimulatie(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        this.tekenaar?.clearResult(this.simulatie);
        cy.nodes().remove();
        this.simulatie = null;
        this.verbergAlleValuehistory();
        this.simulatieStatus.zetHistory({value: false, ineq: false});
        this.updateNextStep();
    }

    /**
     * Zet het simcanvas op slot (replay, `mooieStates`; via `SamenwerkprotocolService.zetSimviewReadonly`):
     * zorg dat niks meer beweegt of selecteert.
     */
    public setReadonly(): void {
        this.cyInstance?.autolock(true);
        this.cyInstance?.autounselectify(true);
    }

    /**
     * Heft het slot van `setReadonly` op: zorg dat alles weer beweegt en selecteert.
     */
    public unsetReadonly(): void {
        this.cyInstance?.autolock(false);
        this.cyInstance?.autounselectify(false);
    }

    /**
     * Vraag het canvas om een resize-operatie uit te voeren.
     */
    public resizeCanvas(): void {
        this.cyInstance?.resize();
    }

    /**
     * Zet een maximumzoom en doe een initiële fit — helper bij `doLayout`, `startSimulatie` en het
     * toepassen van een binnengekomen canvasstand.
     *
     * Via `zetViewport`, want dit scherm staat vaak dicht op het moment dat er getekend wordt: bij een
     * replaystap en bij een volger die aansluit terwijl er een simulatie loopt. Op een container zonder
     * maat legt `cy.center()` de graaf op de linkerbovenhoek — zie de toelichting daar.
     */
    private initZoomEnFit(): void {
        this.zetViewport(() => {
            const cy = this.cyInstance;
            if (!cy) return;
            cy.fit(undefined, 44); // zelfde padding als elders
            const minz = 0.1, maxz = 10;
            const z = cy.zoom();
            if (z < minz) {
                cy.zoom(minz);
            } else if (z > maxz) {
                cy.zoom(maxz);
            }
            cy.center();
        });
    }

    /**
     * Selecteer alle statenodes.
     * @param volger clientid van de volger die hierom vroeg, voor het actielog
     */
    public selecteerAlleStates(volger?: string): void {
        this.voerSelectieactieUit(() => {
            const cy = this.cyInstance;
            if (!cy) return;
            cy.$(':selected').unselect();
            // op numerieke volgorde selecteren én de selectievolgorde numeriek zetten, zodat de
            // history-weergaven (die op selectievolgorde sorteren) de states van 1 tot laatste
            // tonen. De selectie-afhandeling staat onder deze actie stil, dus we zetten de
            // volgorde zelf; de teller schuift mee zodat een handmatige klik erna verder telt.
            let volgorde = 0;
            cy.nodes('[type = "state"]')
                .sort((a, b) => (Number(a.data('num')) || 0) - (Number(b.data('num')) || 0))
                .forEach((state) => {
                    state.data('selectievolgorde', volgorde++);
                    state.select();
                });
            this.selectievolgorde = volgorde;
        }, volger);
    }

    /**
     * Deselecteer alles in het statecanvas.
     * @param volger clientid van de volger die hierom vroeg, voor het actielog
     */
    public selecteerGeenStates(volger?: string): void {
        this.voerSelectieactieUit(() => this.cyInstance?.$(':selected').unselect(), volger);
    }

    /**
     * Selecteer alle eindnodes (leaves) in de stategraph.
     * @param volger clientid van de volger die hierom vroeg, voor het actielog
     */
    public selecteerEindStates(volger?: string): void {
        this.voerSelectieactieUit(() => {
            const cy = this.cyInstance;
            if (!cy) return;
            cy.$(':selected').unselect();
            cy.nodes('[type = "state"]').leaves().select();
        }, volger);
    }

    /**
     * Zoek een pad vanaf `sn0` naar een eindpunt of loop, zodat alle vooraf geselecteerde items
     * erin zitten, en selecteer dat pad. Loopt via dezelfde afwikkeling als de andere drie
     * selectieknoppen — in ng1 ging deze als enige langs de losse selectie-events, waardoor de
     * actielogregel de volger-id miste en de selectievolgorde in eventvolgorde ontstond in plaats
     * van in padvolgorde.
     * @param volger clientid van de volger die hierom vroeg, voor het actielog
     */
    public selecteerPad(volger?: string): void {
        this.voerSelectieactieUit(() => this.zoekEnSelecteerPad(), volger);
    }

    /**
     * Het zoekwerk achter `selecteerPad`: depth-first met dubbele-cycledetectie (een cycle mag
     * hoogstens één keer voorkomen, want soms moet een cycle gebeuren om langs een geselecteerde
     * state te komen).
     */
    private zoekEnSelecteerPad(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const geselecteerd = cy.nodes(':selected').filter('[type = "state"]').map(state => state.id());
        const beste: {route: string[] | null} = {route: null};
        cy.$(':selected').unselect();
        cy.nodes().removeData('padvolgorde');

        const checkRoute = (route: string[]): boolean => {
            if ((!beste.route || beste.route.length > route.length) && geselecteerd.every(s => route.includes(s))) {
                beste.route = route;
                return true;
            }
            return false;
        };

        const zoekRoute = (route: string[]): void => {
            if (beste.route && beste.route.length <= route.length) return;

            const laatste = route.at(-1)!;
            const eersteVoorkomen = route.indexOf(laatste);
            if (eersteVoorkomen < route.length - 1) {
                const cycles: string[] = [];
                let huidigecycle: string | null = null;
                for (let i = 0; i < route.length; i++) {
                    if (route[i] === laatste) {
                        if (huidigecycle !== null) {
                            if (cycles.includes(huidigecycle)) {
                                return; // dubbele cycle: ongeldige route
                            }
                            cycles.push(huidigecycle);
                            huidigecycle = '';
                        } else {
                            huidigecycle = '';
                        }
                    } else if (huidigecycle !== null) {
                        huidigecycle += '-' + route[i];
                    }
                }
            }

            const outIds = cy.$id(route.at(-1)!).outgoers('node[type = "state"]').map(state => state.id());
            if (!outIds.length) {
                checkRoute(route);
                return;
            }
            outIds.sort();
            for (const uitbreiding of outIds) {
                if (route.includes(uitbreiding) && checkRoute([...route, uitbreiding])) {
                    return;
                }
                zoekRoute([...route, uitbreiding]);
            }
        };

        zoekRoute(['sn0']);
        if (beste.route) {
            const route = beste.route;
            for (let nr = 0; nr < route.length; nr++) {
                const stateNode = cy.$id(route[nr]);
                stateNode.select();
                const padvolgorde = (stateNode.data('padvolgorde') as number[]) || [];
                padvolgorde.push(nr);
                stateNode.data('padvolgorde', padvolgorde);
                // de selectie-afhandeling staat stil onder deze actie, dus we zetten de
                // selectievolgorde zelf — op de eerste plek die deze state in de route heeft,
                // zodat een latere handmatige wijziging (die de pad-modus opheft) op dezelfde
                // volgorde terugvalt
                if (padvolgorde.length === 1) {
                    stateNode.data('selectievolgorde', this.selectievolgorde++);
                }
            }
        }
    }

    /**
     * Berekent de vier selectievlaggen van de toolbarknoppen en zet ze in de gedeelde sim-state,
     * waar `dl-sim-toolbar` ze leest.
     */
    private checkStateSelect(): void {
        this.simulatieStatus.zetStateSelectie(this.berekenStateSelectie());
    }

    /**
     * Berekent de vier toolbar-selectievlaggen (alles/niets/endings/pad) en werkt de
     * `opRoute`-edge-class bij.
     */
    private berekenStateSelectie(): StateSelectieVlaggen {
        const cy = this.cyInstance;
        if (!cy) {
            return {alles: false, niets: false, endings: false, pad: false};
        }
        const selectie = cy.nodes('node[type = "state"]:selected');
        const realstates = cy.nodes('node[type = "state"][id != "sn0"]');

        const vlaggen: StateSelectieVlaggen = {
            alles: realstates.nonempty() && realstates.filter(':unselected').empty(),
            niets: realstates.nonempty() && cy.nodes('node[type = "state"]:selected').empty(),
            endings: realstates.filter('[[outdegree = 0]]:selected').nonempty() &&
                realstates.filter('[[outdegree = 0]]:unselected').empty() &&
                cy.nodes('node[type = "state"][[outdegree > 0]]:selected').empty(),
            pad: selectie.roots().length === 1 && selectie.leaves().length === 1 && selectie.every(el => {
                const ele = el as unknown as NodeSingular;
                return (ele.is('[[outdegree = 0]]') || ele.outgoers('node:selected').nonempty()) &&
                    (ele.is('[[indegree = 0]]') || ele.incomers('node:selected').nonempty());
            })
        };

        // edges tussen 2 opvolgende selecties krijgen een extra class
        cy.edges().removeClass('opRoute');
        const statesOpvolgorde = (this.statesVolgensPadvolgorde() ||
            cy.nodes('node[type = "state"]:selected').sort((a, b) =>
                (Number(a.data('selectievolgorde')) || 0) - (Number(b.data('selectievolgorde')) || 0)
            )) as unknown as NodeSingular[];
        for (let i = 0; i < statesOpvolgorde.length - 1; i++) {
            (statesOpvolgorde[i] as unknown as {outElements: (s: string) => cytoscape.NodeCollection}).outElements('edge')
                .intersection((statesOpvolgorde[i + 1] as unknown as {inElements: (s: string) => cytoscape.NodeCollection}).inElements('edge'))
                .addClass('opRoute');
        }

        return vlaggen;
    }

    /**
     * Geeft een array van state-nodes volgens de padvolgorde, of `false` als er geen padvolgorde is.
     * Gebruikt door `berekenStateSelectie()` en `berekenHistoryStates()`.
     * @param skipSN0 Als `true`, dan wordt state-node `sn0` niet meegenomen in de resultaten
     */
    private statesVolgensPadvolgorde(skipSN0?: boolean): NodeSingular[] | false {
        const cy = this.cyInstance;
        if (!cy) return false;
        let opvolgorde: NodeSingular[] = [];
        for (const stateNode of cy.nodes('[padvolgorde]')) {
            if (skipSN0 && stateNode.id() === 'sn0') continue;
            for (const pos of (stateNode.data('padvolgorde') as number[]) || []) {
                opvolgorde[pos] = stateNode;
            }
        }
        opvolgorde = opvolgorde.filter(s => !!s);
        return opvolgorde.length > 0 ? opvolgorde : false;
    }

    /**
     * Werk de edge-classes van een state-node bij op basis van zijn selectiestatus: bij selectie
     * krijgen de uitgaande edges de class `outSelected` en de inkomende `inSelected` (een edge met
     * beide krijgt zo een eigen stijl, zie `sim-style.ts`); bij deselectie vallen die classes weg en
     * wordt de onthouden `padvolgorde` opgeruimd (de plek in een route houdt dan op).
     * @param node de state-node waarvan de selectie wijzigde
     */
    private werkSelectieClassesBij(node: NodeSingular): void {
        const ext = node as unknown as {outElements: (s: string) => cytoscape.NodeCollection; inElements: (s: string) => cytoscape.NodeCollection};
        if (node.selected()) {
            ext.outElements('edge').addClass('outSelected');
            ext.inElements('edge').addClass('inSelected');
        } else {
            ext.outElements('edge').removeClass('outSelected');
            ext.inElements('edge').removeClass('inSelected');
            node.removeData('padvolgorde');
        }
    }

    /* ****************** selectie: gebaar, weergave, log en samenwerking ****************** */

    /**
     * Een state is (de)geselecteerd. De edge-classes trekken altijd bij; de rest — de
     * selectievolgorde en de afhandeling — slaan we over wanneer een programmatische actie loopt,
     * want die wikkelt zelf af. Meerdere events kort na elkaar (multi-select) worden tot één
     * afhandeling gebundeld.
     * @param node de state-node waarvan de selectie wijzigde
     */
    private verwerkSelectie(node: NodeSingular): void {
        if (this.model.fullReadonly) {
            return;
        }
        this.werkSelectieClassesBij(node);
        if (this.eventLock) {
            return;
        }
        // de volgorde van selecteren bewaren we op de node zelf: hij bepaalt de volgorde van de
        // histories en reist mee naar de samenwerkers
        node.data('selectievolgorde', this.selectievolgorde++);
        if (this.selectiePlanner) {
            return;
        }
        this.selectiePlanner = setTimeout(() => {
            this.selectiePlanner = null;
            this.handelSelectieAf();
        });
    }

    /**
     * Handelt een afgeronde reeks selectie-events af: knoppenstate bijwerken, en daarna de route
     * volgen die bij onze rol hoort. Een volger stuurt zijn selectie naar de leider en wacht op
     * diens statusbericht; leider en lokaal tekenen zelf, loggen en (als leider) sturen rond.
     */
    private handelSelectieAf(): void {
        this.checkStateSelect();
        if (this.samenwerking.isVolger()) {
            this.simulatieStatus.vraagSelectie(this.selectieOverdracht());
            return;
        }
        if (this.samenwerking.isLeider()) {
            // eerst sturen, dan pas zelf tekenen: dat scheelt de volgers wachttijd
            this.planStatusVersturen(true);
        }
        this.simulatieStatus.meldSelectie(this.selectieLog());
        this.showResult();
    }

    /**
     * Voert een programmatische selectiewijziging uit (de toolbarknoppen, of een overgenomen
     * selectie van een volger) en wikkelt hem in één keer af: knoppenstate, statusverzending als
     * leider, actielog en de resultaatweergave. De losse selectie-events blijven er onderdrukt,
     * zodat er niet per node gelogd of gestuurd wordt.
     * @param actie de mutatie op het canvas
     * @param volger clientid van de volger die hierom vroeg, voor het actielog
     */
    private voerSelectieactieUit(actie: () => void, volger?: string): void {
        this.eventLock = true;
        actie();
        this.eventLock = false;
        this.checkStateSelect();
        this.planStatusVersturen(true);
        if (!this.samenwerking.isVolger()) {
            this.simulatieStatus.meldSelectie(this.selectieLog(), volger);
        }
        this.showResult();
    }

    /**
     * De huidige stateselectie zoals hij naar de leider gaat: de tellerstand plus per state zijn
     * plek in de selectie- en padvolgorde.
     */
    private selectieOverdracht(): SimSelectieOverdracht {
        const cy = this.cyInstance;
        return {
            selectievolgorde: this.selectievolgorde,
            selected: (cy?.$(':selected') ?? []).map((el: NodeSingular) => ({
                id: el.id(),
                selectievolgorde: el.data('selectievolgorde'),
                padvolgorde: el.data('padvolgorde')
            }))
        };
    }

    /**
     * De inhoud van de actielogregel bij de huidige selectie: bij precies één state de volledige
     * nodedata, anders alleen de ids.
     */
    private selectieLog(): SimSelectieLog {
        const selectie = this.cyInstance?.$(':selected');
        if (!selectie || selectie.length !== 1) {
            return {enkel: null, ids: selectie?.map(el => el.id()) ?? []};
        }
        return {enkel: selectie[0].data(), ids: [selectie[0].id()]};
    }

    /**
     * Neemt de stateselectie van een volger over (leider): dezelfde selectie, dezelfde volgordes,
     * en daarna de gewone afwikkeling met het actielog op naam van die volger.
     * @param selectie de selectie zoals de volger hem stuurde
     * @param volger clientid van de volger
     */
    public pasSelectieToe(selectie: SimSelectieOverdracht, volger?: string): void {
        this.selectievolgorde = selectie.selectievolgorde;
        this.voerSelectieactieUit(() => {
            const cy = this.cyInstance;
            if (!cy) return;
            cy.$(':selected').unselect();
            for (const selectInfo of selectie.selected) {
                const node = cy.$id(selectInfo.id);
                node.select();
                node.data('selectievolgorde', selectInfo.selectievolgorde);
                node.data('padvolgorde', selectInfo.padvolgorde);
            }
        }, volger);
    }

    /**
     * Neemt binnengekomen state-posities over. Het doorsturen naar de andere deelnemers doet de
     * dispatcher van de samenwerk-baas (`simulatietabel`), die de herkomst van het bericht kent.
     * @param posities per node-id de nieuwe positie
     */
    public pasPositiesToe(posities: Record<string, Position>): void {
        const cy = this.cyInstance;
        if (!cy) return;
        for (const [nodeId, positie] of Object.entries(posities)) {
            const node = cy.$id(nodeId);
            if (node.nonempty()) {
                node.position(positie);
            }
        }
    }

    /**
     * Een sleepbeweging is afgerond: de nieuwe posities gaan via `SimulatieStatusService.meldPosities`
     * naar de samenwerk-baas, die ze logt en/of rondstuurt. Het automove-object bevat de versleepte
     * node plus de groep die met hem meebewoog; de ng1-simview las hier `data.all`, een veld dat het
     * automove-object niet (meer) heeft — zie de gelijkvormige lus in `DlModelCanvasComponent.naSlepen`.
     *
     * Daarbovenop sleept Cytoscape zelf de héle selectie mee zodra de gegrabde node geselecteerd
     * is (`possibleDragElements`). Die nodes staan niet in de movegroup, dus we nemen ze er onder
     * diezelfde voorwaarde bij — anders zien de andere clients alleen de aangeklikte state
     * verspringen en blijft de rest van de selectie bij hen staan.
     * @param automove het automove-object van het `endAutomove`-event
     */
    private verwerkVerplaatsing(automove: {mainnode?: NodeSingular; movegroup?: cytoscape.CollectionReturnValue}): void {
        const mainnode = automove?.mainnode;
        if (!mainnode) {
            return;
        }
        const verplaatst: NodeSingular[] = [mainnode, ...(automove.movegroup?.toArray() ?? []) as NodeSingular[]];
        if (mainnode.selected()) {
            verplaatst.push(...(this.cyInstance?.nodes(':selected').toArray() ?? []) as NodeSingular[]);
        }
        const posities: Record<string, Position> = {};
        for (const node of verplaatst) {
            posities[node.id()] = node.position();
        }
        this.simulatieStatus.meldPosities(posities);
    }

    /* ****************** de simulatiestatus over de lijn ****************** */

    /**
     * Plant het rondsturen van de simulatiestatus. Alleen de leider stuurt; meerdere wijzigingen
     * kort na elkaar worden tot één bericht gebundeld, tenzij `direct` gevraagd wordt.
     * @param direct Niet plannen maar meteen sturen
     * @param fit Vraag de ontvangers om de simview opnieuw te fitten
     */
    public planStatusVersturen(direct?: boolean, fit?: boolean): void {
        if (!this.samenwerking.isLeider()) {
            return;
        }
        if (this.statusPlanner) {
            clearTimeout(this.statusPlanner);
            this.statusPlanner = null;
        }
        if (direct) {
            this.samenwerkprotocol.verstuurSimulatiestatus(fit);
            return;
        }
        this.statusPlanner = setTimeout(() => {
            this.statusPlanner = null;
            this.samenwerkprotocol.verstuurSimulatiestatus(fit);
        }, 1);
    }

    /**
     * De canvasstand voor de wire: de geladen simulatie, de geserialiseerde elementen en de
     * tellerstand. `simulatie` is `null` als er niet gesimuleerd wordt; de teller reist altijd mee.
     */
    public haalSimulatiestand(): {simulatie: SimulatieData | null; elements: unknown; selectievolgorde: number;
        invoerhash: string | null} {
        const cy = this.cyInstance;
        return {
            simulatie: this.simulatie,
            // json() is de veilige manier om de inhoud te serialiseren (geen cyclische verwijzingen)
            elements: this.simulatie && cy ? cy.json().elements : undefined,
            selectievolgorde: this.selectievolgorde,
            // de hash reist mee (golf 3.6): daarmee kan de replay bij het terugkeren vaststellen of
            // deze simulatie nog bij het model hoort, zonder de modelversie als proxy te gebruiken
            invoerhash: this.huidigeInvoerhash
        };
    }

    /**
     * Past een binnengekomen canvasstand toe (volger na een statusbericht, of replay): de
     * elementen herladen, de teller overnemen, knoppenstate en volgende stap herberekenen,
     * eventueel fitten en het resultaat hertekenen. Zendt en logt niet — de selectie-events die de
     * herlaad veroorzaakt staan onder de lock, en deze route roept zelf geen meld- of
     * verzendfunctie aan.
     * @param stand de simulatie, de elementen en de tellerstand van de afzender
     * @param fit forceer een fit (nieuw bericht met fit-vlag, of replay)
     */
    public pasSimulatiestandToe(stand: {simulatie: SimulatieData; elements: unknown; selectievolgorde: number;
        invoerhash?: string | null}, fit: boolean): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const nieuwesimulatie = !this.simulatie;
        this.simulatie = stand.simulatie;
        this.selectievolgorde = stand.selectievolgorde;
        this.huidigeInvoerhash = stand.invoerhash ?? null;

        this.eventLock = true;
        // even mogen selecteren en verplaatsen, anders neemt de json-load de elementen niet over
        const locked = cy.autolock();
        cy.autolock(false);
        const unselectify = cy.autounselectify();
        cy.autounselectify(false);
        // json() werkt de elementen bij op id: nieuwe erbij, ontbrekende eruit, bestaande
        // geüpdatet — inclusief specials zoals de padvolgorde. (ng1 deed hier eerst nog een
        // `json({})`; dat is voor de elementen een no-op en is niet meegekomen.)
        cy.json({elements: stand.elements as ElementsDefinition});
        // een json-load vuurt geen selectie-events, dus knoppenstate en volgende stap zelf zetten
        this.checkStateSelect();
        this.updateNextStep();
        cy.autounselectify(unselectify);
        cy.autolock(locked);
        this.eventLock = false;

        if (fit || nieuwesimulatie) {
            this.initZoomEnFit();
        }
        setTimeout(() => this.showResult()); // bijwerken mag async
    }

    /**
     * Ruimt een simulatie op die niet van deze sessie is: de leider simuleert niet meer, of de
     * replay staat op een stap zonder simulatie. Zendt niet, en laat de herstart-cache met opzet
     * staan — die hoort bij de laatste simulatie die de gebruiker zélf sloot, en zou anders de
     * layout van een replaystap of van de leider naar zijn volgende eigen sessie meenemen.
     */
    public ruimSimulatieOp(): void {
        this.wisSimulatie();
    }

    /**
     * Voeg een simulatiestate toe aan de bestaande simulatie. Positie moet apart gebeuren
     * (via `drawPositions`).
     * @param s De state-data uit de simulatie
     * @param status De status van de state (standaard 'open')
     */
    private addState(s: SimulatieState, status: string = 'open'): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const statenode = cy.add({
            group: 'nodes',
            //zonder `sim`: de state is met `num` terug te vinden in `this.simulatie` (`sim-lookup.ts`).
            //Hem hier meegeven zette de hele state in de node-data, en daarmee in elke simulatiestatus
            //die naar een volger of naar een snapshot gaat
            data: {
                type: 'state',
                id: 'sn' + s.num,
                num: s.num,
                status: status,
                automovegroup: 'sn' + s.num,
                automoveorder: 100,
                nomovewithparent: true
            },
            classes: 'state'
        });
        if (s.start) {
            cy.add({
                group: 'edges',
                selectable: false,
                data: {source: 'sn0', target: statenode.id(), id: this.getId('l')},
                classes: 'nextstate'
            });
        }
        this.checkStateLinks(statenode);
    }

    /**
     * Maak een state getermineerd/geordend door de transitienodes te tonen. Vereist daarna
     * herplaatsen via `drawPositions`.
     * @param s De statenode
     * @param transtype 'terminatie', 'ordering' of 'closure' (bij closure alleen de closures
     *   zonder opvolger; tekent zelf geen nieuwe states)
     */
    private simulateTransitie(s: NodeSingular, transtype: 'terminatie' | 'ordering' | 'closure'): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const state = stateVanNode(this.simulatie, s);
        if (!state) return;

        let transities: SimulatieTransitie[];
        let idtype: string;
        switch (transtype) {
            case 'terminatie':
                transities = state.terminaties;
                idtype = 't';
                s.data('status', 'terminated');
                break;
            case 'ordering':
                transities = state.orderings;
                idtype = 'o';
                s.data('status', 'ordered');
                break;
            case 'closure':
                transities = state.closures.filter(c => !c.to.length);
                idtype = 'c';
                s.data('status', 'closed');
                break;
        }

        // verwijder alle oude transities bij deze state
        cy.nodes('[type = "transitie"][statenum = "' + state.num + '"]').unselect().remove();

        transities.forEach((transitie, i) => {
            const id = s.id() + idtype + i;
            cy.add({
                group: 'nodes',
                //zonder `sim` en `statesim`: die twee droegen de transitie én de volledige bronstate,
                //samen driekwart van de simulatiestatus. Wat blijft is genoeg om ze terug te vinden —
                //`statenum`, `transitietype`, `identifier` en de index in het id (`sim-lookup.ts`)
                data: {
                    type: 'transitie',
                    transitietype: transtype,
                    statenum: state.num,
                    id: id,
                    identifier: transitie.identifier,
                    nomovewithparent: true,
                    automovegroup: s.id()
                },
                classes: 'transitie ' + transtype
            });
            cy.add({
                group: 'edges',
                selectable: false,
                data: {weight: 20, source: s.id(), target: id, id: this.getId('l')},
                classes: 'transition'
            });
        });
    }

    /**
     * Sluit een geordende state in de stap-voor-stap-simulatie: toont de closure-transities
     * zonder opvolger en tekent eventuele nieuwe states.
     * @param s De statenode
     */
    private simulateCloseState(s: NodeSingular): void {
        const cy = this.cyInstance;
        if (!cy) return;
        this.simulateTransitie(s, 'closure'); // zet onze state ook op closed

        const state = stateVanNode(this.simulatie, s);
        if (!state) return;
        for (const statenum of state.to) {
            if (cy.getElementById('sn' + statenum).empty()) {
                this.addState(this.simulatie!.states['s' + statenum]); // doet ook de links, want al closed
            }
        }
        this.checkStateLinks(s);
    }

    /**
     * Controleer of we pijlen van/naar de betreffende statenode moeten maken: naar deze node
     * als de voorganger closed is, vanaf deze node als we zelf closed zijn.
     * @param statenode De statenode
     */
    private checkStateLinks(statenode: NodeSingular): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const state = stateVanNode(this.simulatie, statenode);
        if (!state) return;

        for (const num of state.from) {
            const fromnode = cy.getElementById('sn' + num);
            if (fromnode.nonempty() && fromnode.data('status') === 'closed' &&
                cy.edges('[source = "' + fromnode.id() + '"][target = "' + statenode.id() + '"]').empty()) {
                cy.add({
                    group: 'edges',
                    selectable: false,
                    data: {weight: 50, source: fromnode.id(), target: statenode.id(), id: this.getId('l')},
                    classes: 'nextstate'
                });
            }
        }

        if (statenode.data('status') === 'closed') {
            for (const num of state.to) {
                const tonode = cy.getElementById('sn' + num);
                if (tonode.nonempty() &&
                    cy.edges('[source = "' + statenode.id() + '"][target = "' + tonode.id() + '"]').empty()) {
                    cy.add({
                        group: 'edges',
                        selectable: false,
                        data: {weight: 50, source: statenode.id(), target: tonode.id(), id: this.getId('l')},
                        classes: 'nextstate'
                    });
                }
            }
        }
    }

    /**
     * Standaardcode voor het plaatsen van states/transities die nog geen positie hebben.
     * De "mooiere" layout (minimaliseren van lijnkruisingen) zit in `niceStates`.
     */
    private drawPositions(): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const richting = this.schermindeling.richting();

        let repositioned: boolean;
        do {
            repositioned = false;
            cy.nodes('[type = "state"][^geplaatst],[type = "transitie"][^geplaatst]').forEach((stateOfTransitie) => {
                // eerste geplaatste parent zoeken; nog geen parent geplaatst? dan later proberen
                const parentState = (stateOfTransitie as unknown as {inElements: (s: string) => cytoscape.NodeCollection})
                    .inElements('[type = "state"][geplaatst]').first();
                if (parentState.empty()) return;

                const parentPos = parentState.position();
                const subItems = (parentState as unknown as {outElements: (s: string) => cytoscape.NodeCollection})
                    .outElements('[type = "state"][^geplaatst],[type = "transitie"][^geplaatst]');
                const numsub = subItems.length;
                const tussenruimte = subItems.is('[type = "transitie"]') ? 5 : 10;
                const ssize = subItems.first().outerWidth();
                const as1 = numsub * (ssize + tussenruimte) - tussenruimte;

                let plaatspos: Position;
                let plaatsingDx = 0, plaatsingDy = 0;
                const bb = {left: 0, top: 0, right: 0, bottom: 0};

                if (richting === 'verti') {
                    const benodigdehoogte = as1;
                    bb.left = parentPos.x + ssize + tussenruimte;
                    bb.top = parentPos.y - benodigdehoogte / 2;
                    bb.right = bb.left + ssize;
                    bb.bottom = parentPos.y + benodigdehoogte / 2;
                    while (!this.emptyArea(bb.left, bb.right, bb.top, bb.bottom, '[geplaatst]')) {
                        bb.left += tussenruimte;
                        bb.right += tussenruimte;
                    }
                    plaatspos = {x: bb.left + ssize / 2, y: bb.top + ssize / 2};
                    plaatsingDy = ssize + tussenruimte;
                } else {
                    const benodigdebreedte = as1;
                    bb.left = parentPos.x - benodigdebreedte / 2;
                    bb.top = parentPos.y + ssize + tussenruimte;
                    bb.right = parentPos.x + benodigdebreedte / 2;
                    bb.bottom = bb.top + ssize;
                    while (!this.emptyArea(bb.left, bb.right, bb.top, bb.bottom, '[geplaatst]')) {
                        bb.top += tussenruimte;
                        bb.bottom += tussenruimte;
                    }
                    plaatspos = {x: bb.left + ssize / 2, y: bb.top + ssize / 2};
                    plaatsingDx = ssize + tussenruimte;
                }

                subItems.forEach((substate) => {
                    substate.position({...plaatspos});
                    repositioned = true;
                    substate.data('geplaatst', true);
                    plaatspos = {x: plaatspos.x + plaatsingDx, y: plaatspos.y + plaatsingDy};
                });
                return false; // break: helemaal opnieuw zoeken na een batch plaatsingen
            });
        } while (repositioned);
        this.initZoomEnFit();
    }

    /**
     * Bepaal welke paren `nextstate`-edges elkaar geometrisch kruisen (via het snijpunt van de
     * lijnen y = mx + b van beide lijnstukken, met aparte afhandeling voor verticale lijnen).
     * Gebruikt door `niceStates` om de kwaliteit van een layout-poging te meten; alleen het aantal
     * telt.
     */
    private alleKruisingen(): [cytoscape.EdgeSingular, cytoscape.EdgeSingular][] {
        const cy = this.cyInstance;
        if (!cy) return [];

        // welke edges kruizen?
        // dotproduct tussen AB en CD is (B.x-A.x)*(D.x-C.x) + (B.y-A.y)*(D.y-C.y)
        // als dit negatief is, dan kruizen ze
        const edges = cy.edges('.nextstate').toArray();
        const kruisingen: [cytoscape.EdgeSingular, cytoscape.EdgeSingular][] = [];
        let AB: cytoscape.EdgeSingular | undefined;
        while ((AB = edges.shift())) {
            // basisinfo
            const Apos = AB.sourceEndpoint(); // dus niet in de node, maar op de rand
            const Bpos = AB.targetEndpoint();

            // we werken dus met exacte eindpunten
            const ABy_min = Math.min(Apos.y, Bpos.y);
            const ABy_max = Math.max(Apos.y, Bpos.y);
            const ABx_min = Math.min(Apos.x, Bpos.x);
            const ABx_max = Math.max(Apos.x, Bpos.x);

            // zie cytoscape-extensies: geeft een slope (m) en intercept (b) voor y=mx + b; of {x: ...} voor een vertikale lijn
            const AB_f = (AB as unknown as {lijnfunctie: () => {m: number; b: number} | {x: number}}).lijnfunctie();

            for (const CD of edges) {
                // vind het kruispunt op de lijn en bepaal of dat op deze lijn zit
                const Cpos = CD.sourceEndpoint();
                const Dpos = CD.targetEndpoint();

                const CDx_min = Math.min(Cpos.x, Dpos.x);
                const CDx_max = Math.max(Cpos.x, Dpos.x);
                const CDy_min = Math.min(Cpos.y, Dpos.y);
                const CDy_max = Math.max(Cpos.y, Dpos.y);
                const CD_f = (CD as unknown as {lijnfunctie: () => {m: number; b: number} | {x: number}}).lijnfunctie();

                // specials: AB is verticaal
                if ('x' in AB_f) {
                    // CD ook?
                    if ('x' in CD_f) {
                        // allebei verticaal. Ze kruizen niet. Ze lopen parallel, of overlappen
                    } else {
                        // eerste vertikaal, tweede niet
                        // kruispunt is op x = AB_f.x, ligt de bijbehorende y op de lijnen?
                        const y = CD_f.m * AB_f.x + CD_f.b; // de x staat vast, dit is de y
                        // ligt dat op de edge? de y waarde moet tussen ABy_min en ABy_max zitten
                        if (y > ABy_min && y < ABy_max && y > CDy_min && y < CDy_max) {
                            kruisingen.push([AB, CD]);
                        }
                    }
                } else if ('x' in CD_f) {
                    // tweede vertikaal, eerste niet
                    // kruispunt op die x, ligt de bijbehorende y op die lijn?
                    const y = AB_f.m * CD_f.x + AB_f.b;
                    // ligt die x op de eerste edge?
                    if (y > ABy_min && y < ABy_max && y > CDy_min && y < CDy_max) {
                        kruisingen.push([AB, CD]);
                    }
                } else if (AB_f.m !== CD_f.m) {
                    // niet parallel of precies overlappend
                    // ze kruizen ergens in het universum, waar de x en y gelijk zijn
                    // x = (b2 - b1) / (m1 - m2)
                    const x = (CD_f.b - AB_f.b) / (AB_f.m - CD_f.m); // is nooit delen door 0, want dat is parallel
                    const y = AB_f.m * x + AB_f.b;
                    // prima, ligt dat op de lijn?
                    if (x > ABx_min && x < ABx_max && y > ABy_min && y < ABy_max &&
                        x > CDx_min && x < CDx_max && y > CDy_min && y < CDy_max) {
                        // kruist, tenzij in node
                        kruisingen.push([AB, CD]);
                    }
                }
            }
        }
        return kruisingen;
    }
}
